import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FwDialogService } from '@flywheel-io/vision';
import { addWeeks, formatDistanceToNowStrict, getUnixTime, isPast, parseISO } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, combineLatest, defer, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { Userpilot } from 'userpilot';

import { CachingService } from '../caching.service';
import { ConfigService } from '../config.service';
import { CoreApiService, queryAll, WriteResult } from '../core-api.service';
import { FwLegacyDialogService as LegacyDialogService } from '../dialogs/dialog.service';
import { LoggingService } from '../logging.service';
import { BrowserWarningComponent } from '../login/browser-warning.component';
import { NotificationService } from '../notification.service';
import { ContainerAccess } from '../shared/enums/container-access.enum';
import { RequestState } from '../shared/enums/request-state.enum';
import { SiteRole } from '../shared/enums/site-role.enum';
import { ApiKey } from '../shared/models/api-key.model';
import { Collection } from '../shared/models/collection.model';
import { Device } from '../shared/models/device.model';
import { Group } from '../shared/models/group.model';
import { MultifactorSettings, Profile } from '../shared/models/profile.model';
import { UserPreferences } from '../shared/models/user.model';
import { UserSettings } from '../shared/models/user-settings.model';
import { XferStoreSCU } from '../shared/models/xfer.model';
import { checkLocalStorageForAuth, scrubAuthFromLocalStorage } from '../shared/token.utils';
import {
  ExpiredApiKey,
  ExpiredApiKeysDialogComponent,
  ExpiredApiKeysDialogData,
  IGNORE_EXPIRATION_KEY,
} from './expired-api-keys-dialog/expired-api-keys-dialog.component';
import {
  defaultServerSettings,
  defaultSessionSettings,
  getLocalStorageSettings,
  normalizeTaskManagerSortOrder,
  USER_SETTINGS_KEY,
} from './setting.utils';
@Injectable({
  providedIn: 'root',
})
export class ProfileService extends CachingService {
  private config = inject(ConfigService);
  private legacyDialog = inject(LegacyDialogService);
  private dialog = inject(FwDialogService);
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private router = inject(Router);

  private hasWarnedForExpiringKeys = false;

  private profile = {
    state: RequestState.Empty,
    subject: new BehaviorSubject<Profile | undefined>(undefined),
    // defer so the underlying subscription fires again every time refresh is triggered
    request: combineLatest([this.coreApi.users.self(), defer(() => this.mfaSettingsOnceConfigLoaded())]).pipe(
      catchError((error) => {
        this.logger.error(`Failed to load user profile: ${error.status}, ${error.message}`);
        if (error.status === 409) {
          // attempting to prevent an infinite refresh loop by checking for some form of auth
          if (checkLocalStorageForAuth()) {
            scrubAuthFromLocalStorage();
            window.location.assign(window.location.href);
          }
        }
        throw error;
      }),
      // copy so a later emission with mfa settings isn't the same reference as the one without
      map(([profile, multifactor]) => (multifactor ? { ...profile, multifactor: multifactor[0] || {} } : profile)),
    ),
  };
  readonly profile$ = this.profile.subject.pipe(
    tap((user) => {
      // associate errors to the current user
      // TODO: add back sentry user when we turn sentry back on after angular update
      // Sentry.setUser({ id: user?.user_id });
      // set userpilot user data
      // the userpilot flag defaults on until the api config loads, so wait for the real value
      if (this.config.apiConfig && this.config.isFeatureEnabled('userpilot')) {
        this.config
          .getVersion()
          .pipe(first())
          .subscribe((version) => {
            if (user) {
              Userpilot.identify(user.email, {
                roles: user.roles,
                version: version?.flywheel_release,
              });
            } else {
              Userpilot.anonymous({ version: version?.flywheel_release });
            }
          });
      }
      if (user?.preferences) {
        const updatedSettings: UserSettings = { ...this.settings.value, ...user.preferences };
        if ('task_manager_column_sort_order' in user.preferences) {
          updatedSettings.task_manager_column_sort_order = normalizeTaskManagerSortOrder(
            user.preferences.task_manager_column_sort_order,
          );
        }
        if (!isEqual(updatedSettings, this.settings.value)) {
          this.settings.next(updatedSettings);
        }
      }
    }),
    shareReplay(1),
  );

  public readonly profileSignal = toSignal(this.profile$);

  private settings = new BehaviorSubject<UserSettings>(getLocalStorageSettings());
  readonly settings$ = this.settings.asObservable();

  readonly siteAdmin$ = defer(() => this.getProfile()).pipe(
    distinctUntilChanged(),
    filter((profile) => Boolean(profile)), // wait until we have a user established
    map((profile) => SiteRole.isSiteAdmin(profile)),
  );
  readonly siteAdmin = toSignal(this.siteAdmin$, { initialValue: false });

  // Emits true if the user is an admin with "showAllData" enabled
  readonly exhaustiveMode$: Observable<boolean> = combineLatest([this.siteAdmin$, this.settings$]).pipe(
    map(([isSiteAdmin, settings]) => isSiteAdmin && settings.showAllData),
    distinctUntilChanged(),
  );

  readonly exhaustiveMode = toSignal(this.exhaustiveMode$, { initialValue: false });

  private userTaskCount = {
    state: RequestState.Empty,
    subject: new BehaviorSubject<number>(undefined),
    request: defer(() => this.getProfile()).pipe(
      switchMap((profile) =>
        profile
          ? this.coreApi.readerTasks
              .query({
                filter: `assignee=${profile._id},status=|[Todo,In_progress]`,
              })
              .pipe(map(({ total }) => total))
          : of(0),
      ),
    ),
  };
  readonly userTaskCount$ = this.userTaskCount.subject.asObservable();
  private expiredKeysDialogIsOpen = false;

  constructor() {
    super();

    this.checkBrowserCompatability();

    this.exhaustiveMode$
      .pipe(
        skip(1), // skip the emission on app init
      )
      .subscribe((exhaustiveModeEnabled) => {
        if (!exhaustiveModeEnabled) {
          // retrigger route guards when we disable exhaustive mode
          this.router.navigateByUrl(this.router.url, {
            onSameUrlNavigation: 'reload',
          });
        }
      });
  }

  set showAllData(value: boolean) {
    this.updateSettings({ showAllData: value });
  }

  /**
   * The multifactor_auth flag comes from the api config, which loads after auth in the root route's resolver,
   * but the profile is first requested at bootstrap. In that case emit without mfa settings right away
   * (guards that run before the resolver need the profile), then again once the flag is known.
   */
  private mfaSettingsOnceConfigLoaded(): Observable<MultifactorSettings> {
    if (this.config.apiConfig) {
      return this.mfaSettings();
    }
    return this.config.apiConfig$.pipe(
      first(),
      switchMap(() => this.mfaSettings()),
      startWith(undefined),
    );
  }

  mfaSettings(): Observable<MultifactorSettings> {
    if (this.config.isFeatureEnabled('multifactor_auth')) {
      return this.coreApi.users.mfaSettings.get().pipe(
        catchError((error) => {
          if (error.status === 404) {
            return of(undefined);
          }
          throw error;
        }),
      );
    } else {
      return of(undefined);
    }
  }

  openExpiringKeysDialog(): void {
    combineLatest([
      this.profile$.pipe(filter((profile) => Boolean(profile))),
      queryAll(this.coreApi.devices.query, { join_keys: true }).pipe(
        catchError(() => {
          return of([]); // ignore 403 errors
        }),
      ) as Observable<Device[]>,
      (this.config.isFeatureEnabled('dicom_push_connector')
        ? queryAll(this.coreApi.xfer.storeSCUS.query, { join_keys: true }).pipe(catchError(() => of([])))
        : of([])) as Observable<XferStoreSCU[]>,
    ])
      .pipe(first())
      .subscribe(([profile, devices, storeSCUS]) => {
        if (this.hasWarnedForExpiringKeys) {
          return;
        }

        const expiringUserKeys = this.checkForExpiringKeys(profile?.api_keys);
        let expiringDeviceKeys: ExpiredApiKey[] = [];
        for (const device of devices) {
          const myDeviceKeys = this.checkForExpiringKeys(device?.api_keys);
          if (myDeviceKeys) {
            expiringDeviceKeys = [...expiringDeviceKeys, ...myDeviceKeys];
          }
        }

        const expiringDeviceMTLSCertificates: ExpiredApiKey[] = [];
        for (const item of storeSCUS) {
          const expiredApiKey = this.checkForExpiringCertificates(item);
          if (expiredApiKey) {
            expiringDeviceMTLSCertificates.push(expiredApiKey);
          }
        }

        if (
          !this.expiredKeysDialogIsOpen &&
          (expiringUserKeys?.length || expiringDeviceKeys?.length || expiringDeviceMTLSCertificates.length)
        ) {
          this.expiredKeysDialogIsOpen = true;
          const data: ExpiredApiKeysDialogData = {
            userKeys: expiringUserKeys,
            deviceKeys: expiringDeviceKeys,
            deviceMTLSCertificates: expiringDeviceMTLSCertificates,
          };
          this.dialog.open(ExpiredApiKeysDialogComponent, {
            data,
            disableClose: true,
          });
          this.hasWarnedForExpiringKeys = true;
        }
      });
  }

  checkForExpiringKeys(keys: ApiKey[]): ExpiredApiKey[] {
    if (!keys || keys?.length === 0) {
      return [];
    }

    const ignoredKeys: string[] = JSON.parse(localStorage.getItem(IGNORE_EXPIRATION_KEY)) || [];
    const expiringKeys: ExpiredApiKey[] = [];
    for (const key of keys) {
      const expireDate = key.expires_at ? parseISO(key.expires_at) : null;
      const isExpired = isPast(expireDate);
      const expiresSoon = expireDate
        ? isExpired || getUnixTime(expireDate) - getUnixTime(addWeeks(new Date(), 1)) < 0
        : false;
      if (!ignoredKeys.includes(key.key) && (isExpired || expiresSoon)) {
        expiringKeys.push({
          label: key.label,
          key: key.key,
          isExpired: isExpired,
          formattedDate: expireDate ? formatDistanceToNowStrict(expireDate) : null,
        } as ExpiredApiKey);
      }
    }
    return expiringKeys;
  }

  checkForExpiringCertificates(scu: XferStoreSCU): ExpiredApiKey {
    const ignoredKeys: string[] = JSON.parse(localStorage.getItem(IGNORE_EXPIRATION_KEY)) || [];
    const expireDate = new Date(scu.cert_expiration);
    const isExpired = isPast(expireDate);
    const expiresSoon = expireDate
      ? isExpired || getUnixTime(expireDate) - getUnixTime(addWeeks(new Date(), 1)) < 0
      : false;

    if (ignoredKeys.includes(scu.cert.pem) || (!isExpired && !expiresSoon)) {
      return null;
    }

    return {
      label: scu.name,
      key: scu.cert.pem,
      isExpired: isExpired,
      formattedDate: expireDate ? formatDistanceToNowStrict(expireDate) : null,
    } as ExpiredApiKey;
  }

  checkBrowserCompatability(): void {
    if (!this.settings.value.dismissBrowserWarning) {
      const browserInfo =
        navigator.userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
      const isChrome = /chrome/i.test(browserInfo[1]);
      // include special case for Edge because it tries to pretend it's Chrome.
      const promise =
        !isChrome || navigator.userAgent.includes('Edge')
          ? this.legacyDialog
              .open(BrowserWarningComponent, { width: '500px', disableClose: false })
              .afterClosed()
              .toPromise()
          : Promise.resolve();
      promise.then(() => {
        this.updateSettings({ dismissBrowserWarning: true });
      });
    }
  }

  updateSettings(updates: Partial<UserSettings>): void {
    const settings = { ...this.settings.value, ...updates };
    if (!isEqual(settings, this.settings.value)) {
      this.saveSettings(settings);
      this.settings.next(settings);
    }
  }

  getProfile({ refresh = false } = {}): Observable<Profile | undefined> {
    return this.getCacheableResource(this.profile, this.profile$, { refresh });
  }

  // Many AngularJS controllers just need one time promises that ensure they're getting a fresh profile without cleaning up a subscription.
  getProfilePromise(): Promise<Profile> {
    // Due to how .toPromise works, we need first in the pipe
    // https://github.com/ReactiveX/rxjs/issues/2536#issuecomment-292829380
    return this.getProfile()
      .pipe(first((data) => Boolean(data)))
      .toPromise();
  }

  getUserTaskCount({ refresh = false } = {}): Observable<number> {
    return this.getCacheableResource(this.userTaskCount, this.userTaskCount$, { refresh });
  }

  generateApiKey(): Observable<string> {
    return this.coreApi.users.apiKeys.generateKey().pipe(map(({ key }) => key));
  }

  /**
   * Clear the profile state only. Called from AuthService.logout(), which also clears auth state.
   */
  logout(): void {
    this.profile.state = RequestState.Empty;
    this.profile.subject.next(undefined);
    this.updateSettings(defaultSessionSettings);
  }

  /**
   * @deprecated use granular permission checks
   */
  canAdminContainer(container: Collection | Group): boolean {
    if (!this.profile.subject.value) {
      return false;
    }
    if (this.siteAdmin()) {
      return true;
    }
    return (container?.permissions ?? []).some(
      (permission) => permission._id === this.profile.subject.value._id && permission.access === ContainerAccess.Admin,
    );
  }

  putProfile(data): Observable<WriteResult> {
    return this.coreApi.users.put(this.profile.subject.value._id, data).pipe(
      tap(() => {
        this.getProfile({ refresh: true });
      }),
    );
  }

  saveSettings(settings: UserSettings): void {
    const sessionSettings: Partial<UserSettings> = {};
    const localSettings: Partial<UserSettings> = {};
    const serverSettings: Partial<UserPreferences> = {};
    for (const key in settings) {
      if (key in defaultSessionSettings) {
        sessionSettings[key] = settings[key];
      } else if (key in defaultServerSettings) {
        serverSettings[key] =
          key === 'task_manager_column_sort_order' ? normalizeTaskManagerSortOrder(settings[key]) : settings[key];
      } else {
        localSettings[key] = settings[key];
      }
    }
    sessionStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(sessionSettings));
    localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(localSettings));
    const lastServerSettings: Partial<UserSettings> = {};
    for (const key in this.settings.value) {
      if (key in defaultServerSettings) {
        lastServerSettings[key] = this.settings.value[key];
      }
    }
    if (!isEqual(lastServerSettings, serverSettings)) {
      this.coreApi.users.updatePreferences(serverSettings as UserPreferences).subscribe({
        error: (error) => {
          this.notifications.error('profile.updatePreferences.error');
          this.logger.error(error);
        },
      });
    }
  }

  /**
   * Checks the feature flag and the user's role to determine if the support link should be hidden.
   * @remarks Some customers have internal support flows and want to prevent users from directly contacting Flywheel support.
   */
  hideSupportLinks = computed(() => {
    const hideSupportLinkFeature = this.config.getFeatureSetting('support_link_admins_only');
    const isAdmin = this.siteAdmin();

    return Boolean(hideSupportLinkFeature && !isAdmin);
  });
}
